import { useState } from 'react'
import { CalendarRange, Check, ClipboardList } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { IntuiBadge } from '@/components/intueri/IntuiBadge/IntuiBadge'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import type { Period, RunState } from '../v4/contracts'
import { summaryText } from '../v4/answer-text'
import { AGENT_NAME } from './agentName'
import AgentDot from './AgentDot'
import RerunBar from './RerunBar'
import { canRerun } from './runRules'
import { outcomeTone } from './runStory'

/** Findings shown on the card; the report has them all. */
const SHOWN_FINDINGS = 3

/**
 * The run's end in the chat (the old result card, Figma "8. Report"): the outcome as a badge, the
 * answer's headline and summary, its first findings, the counts, and View report, which opens the
 * Report tab. A run that ended without an answer (cancelled, refused, blocked) shows the engine's
 * line instead; a cancelled run with an answer shows both.
 */
export default function ResultCard({
  run,
  text,
  busy = false,
  onViewReport,
  onRerun,
}: {
  run: RunState
  /** The engine's line for the finish (v4/conversation-text.ts), if it has one. */
  text: string | null
  busy?: boolean
  onViewReport?: () => void
  /** The approved plan again on another period, as a new run; the card folds the dates out. */
  onRerun?: (period: Period) => Promise<unknown>
}) {
  const { t } = useTranslation()
  const [rerunOpen, setRerunOpen] = useState(false)
  const rerunnable = Boolean(onRerun && canRerun(run))
  const a = run.trace.answer
  const summary = a ? summaryText(a.headline, a.prose) : ''
  const counts = a
    ? [
        a.findings.length > 0 && t('workflow:builder.n_findings', { defaultValue: '{{count}} finding(s)', count: a.findings.length }),
        a.actions.length > 0 && t('workflow:builder.n_actions', { defaultValue: '{{count}} action(s)', count: a.actions.length }),
        a.unknowns.length > 0 && t('workflow:builder.n_unknowns', { defaultValue: '{{count}} still unknown', count: a.unknowns.length }),
      ].filter((x): x is string => typeof x === 'string')
    : []
  const answered = run.outcome === 'satisfied' || run.outcome === 'partial'
  const outcomeKey: string = `workflow:outcome.${run.outcome ?? ''}`

  return (
    <div className="rounded-lg border border-border bg-background p-3.5 dark:bg-dark-card">
      <div className="flex items-center gap-2">
        <AgentDot />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {AGENT_NAME}: {t('workflow:builder.result_done', { defaultValue: 'Done' })}
        </span>
        {run.outcome && (
          <IntuiBadge variant={outcomeTone(run.outcome)}>
            {String(t(outcomeKey, { defaultValue: run.outcome }))}
          </IntuiBadge>
        )}
      </div>
      {text && (!a || !answered) && <p className="mt-2.5 text-sm text-foreground">{text}</p>}
      {a && (
        <>
          <p className="mt-2.5 text-sm font-semibold leading-snug text-foreground">{a.headline}</p>
          {summary && <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{summary}</p>}
          {a.findings.length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {a.findings.slice(0, SHOWN_FINDINGS).map((f) => (
                <li key={f.id} className="flex gap-2 text-sm text-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-500" aria-hidden />
                  <span>{f.statement}</span>
                </li>
              ))}
            </ul>
          )}
          {counts.length > 0 && <p className="mt-2.5 text-xs text-muted-foreground">{counts.join(' · ')}</p>}
          {(onViewReport || rerunnable) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {onViewReport && (
                <IntuiButton size="sm" leftIcon={<ClipboardList />} onClick={onViewReport}>
                  {t('workflow:builder.view_report', { defaultValue: 'View report' })}
                </IntuiButton>
              )}
              {rerunnable && (
                <IntuiButton size="sm" variant="outline" leftIcon={<CalendarRange />} aria-expanded={rerunOpen} onClick={() => setRerunOpen((v) => !v)}>
                  {t('workflow:builder.rerun_open', { defaultValue: 'Run again on another period' })}
                </IntuiButton>
              )}
            </div>
          )}
        </>
      )}
      {rerunnable && rerunOpen && onRerun && (
        <div className="mt-3">
          <RerunBar run={run} busy={busy} onRerun={onRerun} />
        </div>
      )}
    </div>
  )
}
